import { createElement, type ReactNode } from 'react'
import {
  $applyNodeReplacement,
  $createTextNode,
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type TextFormatType
} from 'lexical'
import { getContractVariable } from '@super-mega/server/contracts/contract'
import { ContractVariableToken } from './contract-variable-token.tsx'

export type ContractVariableOption = {
  key: string
  label: string
}

type SerializedContractVariableNode = {
  detail: number
  format: number
  mode: 'normal' | 'token' | 'segmented'
  style: string
  text: string
  type: 'contract-variable'
  variableKey: string
  version: 1
}

/** 行内原子字段：正文布局不换行，字段内部不承载文本选区。 */
export class ContractVariableNode extends DecoratorNode<ReactNode> {
  __variableKey: string
  __text: string
  __format: number
  __style: string

  static getType() {
    return 'contract-variable'
  }
  static clone(node: ContractVariableNode) {
    return new ContractVariableNode({
      text: node.__text,
      variableKey: node.__variableKey,
      format: node.__format,
      style: node.__style,
      key: node.__key
    })
  }
  constructor({
    text,
    variableKey,
    format = 0,
    style = '',
    key
  }: {
    text: string
    variableKey: string
    format?: number
    style?: string
    key?: NodeKey
  }) {
    super(key)
    this.__text = text
    this.__variableKey = variableKey
    this.__format = format
    this.__style = style
  }
  static importJSON(serialized: SerializedContractVariableNode) {
    const variable = getContractVariable(serialized.variableKey)
    return $applyNodeReplacement(
      new ContractVariableNode({
        text: variable ? `〔${variable.label}〕` : serialized.text,
        variableKey: serialized.variableKey,
        format: serialized.format,
        style: serialized.style
      })
    )
  }
  exportJSON(): SerializedContractVariableNode {
    return {
      ...super.exportJSON(),
      type: 'contract-variable',
      version: 1,
      text: this.getTextContent(),
      variableKey: this.getLatest().__variableKey,
      format: this.getFormat(),
      style: this.getStyle(),
      detail: 0,
      mode: 'token'
    }
  }
  getTextContent() {
    return this.getLatest().__text
  }
  getFormat() {
    return this.getLatest().__format
  }
  getStyle() {
    return this.getLatest().__style
  }
  setFormat(format: number | TextFormatType) {
    this.getWritable().__format =
      typeof format === 'number' ? format : $createTextNode('').setFormat(format).getFormat()
    return this
  }
  setStyle(style: string) {
    this.getWritable().__style = style
    return this
  }
  createDOM() {
    const dom = document.createElement('span')
    dom.contentEditable = 'false'
    dom.className = 'inline-block select-none align-baseline'
    dom.dataset.variableKey = this.__variableKey
    dom.title = `{{${this.__variableKey}}}`
    dom.style.cssText = this.__style
    if (this.__format & 1) {
      dom.style.fontWeight = 'bold'
    }
    if (this.__format & 2) {
      dom.style.fontStyle = 'italic'
    }
    dom.style.textDecoration = [this.__format & 4 ? 'line-through' : '', this.__format & 8 ? 'underline' : '']
      .filter(Boolean)
      .join(' ')
    return dom
  }
  updateDOM(previous: ContractVariableNode) {
    return previous.__format !== this.__format || previous.__style !== this.__style
  }
  exportDOM() {
    const element = this.createDOM()
    element.textContent = this.getTextContent()
    return { element }
  }
  decorate() {
    return createElement(ContractVariableToken, { nodeKey: this.getKey(), text: this.getTextContent() })
  }
}

export function $isContractVariableNode(node: LexicalNode | null | undefined): node is ContractVariableNode {
  return node instanceof ContractVariableNode
}
export function $createContractVariableNode(variable: ContractVariableOption) {
  return $applyNodeReplacement(new ContractVariableNode({ text: `〔${variable.label}〕`, variableKey: variable.key }))
}
